import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../Common/components/Inputs/Input/Input";
import t from "../../../Common/utils/translation/translator";
import { selectDynamics, setDynamicsCycleRunning } from "../../store/slices/cartesian";
import { maxNumberCyclesPerHour, preCalcTotalCycleTime } from "../../utils/func.utils";
import SectionHeader from "../SectionHeader";
import "./CycleRunning.scss";

const CycleRunning = ({ ...props }) => {
  const dispatch = useDispatch();
  const dynamics = useSelector(selectDynamics);
  const moves = dynamics.steps;
  const cycleRunning = dynamics?.cycleRunning;

  // Track manual updates
  const [isManualUpdate, setIsManualUpdate] = useState(false);

  const handleInputChange = (property) => (e) => {
    setIsManualUpdate(true); // Mark as manually updated
    dispatch(
      setDynamicsCycleRunning({
        ...cycleRunning,
        [property]: e.target.value === "" ? e.target.value : Number(e.target.value)
      })
    );
  };

  /**
   * Function adaptValue adapt value for "numberOfCyclePerHour" input only if needed,
   * returns other value instead.
   *
   * @param {*} input
   * @returns int
   */
  const adaptValue = (input) => {
    let value = cycleRunning?.[input.property];

    if (
      input.property === "numberOfCyclePerHour" &&
      !isManualUpdate && // Only adapt if not manually updated
      (value === undefined || value === "")
    ) {
      value = input.max / 2;
      if (value !== Infinity) {
        dispatch(
          setDynamicsCycleRunning({
            ...cycleRunning,
            [input.property]: Number(value)
          })
        );
      }
    }
    return value;
  };

  const inputs = [
    {
      property: "numberOfCyclePerHour",
      label: t("cartesian.ui.dynamics.number_of_cycle/hour"),
      min: 1,
      max: maxNumberCyclesPerHour(moves),
      invalidText:
        preCalcTotalCycleTime(moves) > 3600000
          ? t("cartesian.ui.dynamics.total_cycle_time_exceed_1h")
          : t("cartesian.ui.dynamics.cycle.val-1-max", {
              max: maxNumberCyclesPerHour(moves)
            })
    },
    {
      property: "workingHoursPerDay",
      label: t("cartesian.ui.dynamics.working_hours/day"),
      min: 1,
      max: 24,
      invalidText: t("cartesian.ui.dynamics.cycle.val-1-24")
    },
    {
      property: "workingDaysPerYear",
      label: t("cartesian.ui.dynamics.working_days/year"),
      min: 1,
      max: 365,
      invalidText: t("cartesian.ui.dynamics.cycle.val-1-365")
    }
  ];

  return (
    <div className="CycleRunning" id="cycle-running" {...props}>
      <SectionHeader data-testid="section-header-cycle-running" title={t("cartesian.ui.dynamics.cycle_running")} />
      <div className="flex space-x-5 p-2 mt-1 reference-value">
        {t("cartesian.ui.dynamics.total_cycle_time_val_ms", { val: preCalcTotalCycleTime(moves) })}
      </div>
      <div className="flex space-x-5 p-2 mt-1">
        {inputs.map((input, index) => (
          <Input
            data-testid={`input-${input.property}`}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            type="number"
            pattern="[0-9]*"
            onChange={handleInputChange(input.property)}
            name={input.property}
            value={adaptValue(input)}
            label={input.label}
            classes="max-w-[225px]"
            min={input.min}
            max={input.max}
            minMaxTopLabelShow
            mandatory
            noDecimals
            disabled={input.property === "numberOfCyclePerHour" && preCalcTotalCycleTime(moves) > 3600000}
            isInvalid={
              (input.property === "numberOfCyclePerHour" && preCalcTotalCycleTime(moves) > 3600000) ||
              !cycleRunning?.[input.property] ||
              cycleRunning?.[input.property] < input.min ||
              cycleRunning?.[input.property] > input.max
            }
            invalidText={input.invalidText}
          />
        ))}
      </div>
    </div>
  );
};

export default CycleRunning;
